<script setup lang="ts">
import { computed } from 'vue'

/**
 * The one button.
 *
 * `danger` is a real variant rather than a colour someone remembers to apply, because the actions
 * that need it — the ones that change a live client site — are the ones where a forgotten style
 * is a real cost.
 */

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
    size?: 'sm' | 'md'
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    block?: boolean
    /** Rendered as an anchor when set. Same styling, correct semantics. */
    href?: string
    title?: string
  }>(),
  { variant: 'secondary', size: 'md', type: 'button', disabled: false, loading: false, block: false },
)

defineEmits<{ click: [MouseEvent] }>()

const tag = computed(() => (props.href ? 'a' : 'button'))
const inert = computed(() => props.disabled || props.loading)
</script>

<template>
  <component
    :is="tag"
    class="o-button"
    :class="[`v-${variant}`, `s-${size}`, { block, loading }]"
    :type="href ? undefined : type"
    :href="href"
    :disabled="href ? undefined : inert"
    :aria-disabled="inert ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    :title="title"
    @click="!inert && $emit('click', $event)"
  >
    <span v-if="loading" class="spinner" aria-hidden="true" />
    <slot name="icon" />
    <span class="label"><slot /></span>
    <slot name="trailing" />
  </component>
</template>

<style scoped>
.o-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border-radius: var(--radius-md);
  border: var(--border-width) solid transparent;
  font-weight: var(--weight-medium);
  font-size: var(--text-sm);
  line-height: 1;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out);
}

.o-button:hover {
  text-decoration: none;
}

.s-md {
  padding: 0 var(--space-4);
  height: 2.125rem;
}

.s-sm {
  padding: 0 var(--space-3);
  height: 1.75rem;
  font-size: var(--text-xs);
}

.block {
  width: 100%;
}

.v-primary {
  background: var(--accent);
  color: var(--text-inverted);
  border-color: var(--accent);
}

.v-primary:hover:not([aria-disabled='true']) {
  background: var(--accent-hover);
  border-color: var(--accent-hover);
}

.v-secondary {
  background: var(--bg-surface);
  color: var(--text-default);
  border-color: var(--border-strong);
}

.v-secondary:hover:not([aria-disabled='true']) {
  background: var(--bg-hover);
  color: var(--text-strong);
}

.v-ghost {
  background: transparent;
  color: var(--text-muted);
}

.v-ghost:hover:not([aria-disabled='true']) {
  background: var(--bg-hover);
  color: var(--text-strong);
}

.v-danger {
  background: var(--critical);
  color: var(--text-inverted);
  border-color: var(--critical);
}

.v-danger:hover:not([aria-disabled='true']) {
  filter: brightness(1.08);
}

.o-button[aria-disabled='true'],
.o-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.spinner {
  width: 0.875em;
  height: 0.875em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: var(--radius-full);
  animation: spin 0.7s linear infinite;
  flex: none;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
